import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import axios from 'axios';

// Components
import Navbar from './components/layout/Navbar';
import Sidebar from './components/layout/Sidebar';
import Dashboard from './components/dashboard/Dashboard';
import ReturnsTable from './components/returns/ReturnsTable';
import CreateReturn from './components/returns/CreateReturn';
import ReturnDetails from './components/returns/ReturnDetails';
import EnhancedReturnDetails from './components/returns/EnhancedReturnDetails';
import OrderDetails from './components/orders/OrderDetails';
import Settings from './components/settings/Settings';
import Login from './components/auth/Login';
import UserAuditLog from './components/admin/UserAuditLog';
import OperationsWorkspace from './components/operations/OperationsWorkspace';
import Reports from './components/reports/Reports';
import { ReturnsProvider } from './contexts/ReturnsContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1A365D', // Dark Navy Blue
      light: '#2D4A66',
      dark: '#0D2438',
    },
    secondary: {
      main: '#A0AEC0', // Silver/Light Blue-Grey
      light: '#CBD5E0',
      dark: '#718096',
    },
    background: {
      default: '#F7FAFC', // Very light blue-grey
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A202C', // Dark blue-grey
      secondary: '#4A5568',
    },
    info: {
      main: '#3182CE', // Medium blue
      light: '#63B3ED',
      dark: '#2C5282',
    },
    success: {
      main: '#38A169',
      light: '#68D391',
      dark: '#2F855A',
    },
    warning: {
      main: '#D69E2E',
      light: '#F6E05E',
      dark: '#B7791F',
    },
    error: {
      main: '#E53E3E',
      light: '#FC8181',
      dark: '#C53030',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
  },
});

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('sessionToken');
      if (!token) {
        setLoading(false);
        return;
      }
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      try {
        const response = await axios.get('/api/auth/verify');
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setUser(response.data.user);
      } catch (error) {
        delete axios.defaults.headers.common.Authorization;
        localStorage.removeItem('user');
        localStorage.removeItem('sessionToken');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (error) {
      // Always clear local state even if the server session has already expired.
    }
    delete axios.defaults.headers.common.Authorization;
    localStorage.removeItem('user');
    localStorage.removeItem('sessionToken');
    setUser(null);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Loading...
        </Box>
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login onLoginSuccess={handleLogin} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ReturnsProvider>
        <Router>
          <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <Navbar onMenuClick={toggleSidebar} user={user} onLogout={handleLogout} />
            <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} user={user} />
            
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                pt: '80px' // Add top padding to account for fixed navbar (64px toolbar + 16px margin)
              }}
            >
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/returns" element={<ReturnsTable />} />
                <Route path="/operations" element={<OperationsWorkspace />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/returns/new" element={<CreateReturn />} />
                <Route path="/returns/:id" element={<EnhancedReturnDetails />} />
                <Route path="/returns/:id/edit" element={<ReturnDetails />} />
                <Route path="/orders/:platform/:id" element={<OrderDetails />} />
                <Route path="/settings" element={user.role === 'admin' ? <Settings /> : <Dashboard />} />
                <Route path="/admin/audit-log" element={user.role === 'admin' ? <UserAuditLog /> : <Dashboard />} />
              </Routes>
            </Box>
          </Box>
        </Router>
      </ReturnsProvider>
    </ThemeProvider>
  );
}

export default App;
